import { useSyncExternalStore } from "react";

/**
 * Light / dark / follow-the-system, for the whole platform.
 *
 * Three states rather than a boolean: "system" is the honest default, and a
 * two-way toggle forces a choice on somebody who has already expressed one at
 * the OS level. The explicit settings win over the system when chosen.
 *
 * The class goes on <html> because that is what the token blocks key off --
 * every semantic colour in the app resolves through those variables, so one
 * class flip repaints the whole product with no per-component `dark:` variants.
 *
 * The preference belongs to the person, not to the product they happened to set
 * it in: it is one of "your settings", which follow you across the Helpdesk and
 * the CRM. Since people keep both open in separate tabs, that has to mean every
 * open tab and not just the one holding the radio button -- hence a module-level
 * store rather than component state. Tabs learn about each other through the
 * `storage` event, which fires in every *other* tab of the origin; the tab that
 * made the change is updated directly, because it never receives its own event.
 */
export type Theme = "light" | "dark" | "system";

const KEY = "helpdesk.theme";

function read(): Theme {
  try {
    const raw = localStorage.getItem(KEY);
    return raw === "light" || raw === "dark" ? raw : "system";
  } catch {
    // Safari private mode throws on localStorage. A theme preference is never
    // worth breaking the app over.
    return "system";
  }
}

let current: Theme = read();

const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Resolve to the class that should actually be on <html> right now. */
function resolve(theme: Theme): "light" | "dark" {
  if (theme !== "system") return theme;
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

export function applyTheme(theme: Theme): void {
  document.documentElement.classList.toggle("dark", resolve(theme) === "dark");
}

/** The preference as it stands, for a caller outside React. */
export function currentTheme(): Theme {
  return current;
}

export function setTheme(theme: Theme): void {
  if (theme === current) return;
  current = theme;

  try {
    localStorage.setItem(KEY, theme);
  } catch {
    // See `read`. The choice still applies to this tab; it just will not
    // survive a reload or reach the others.
  }

  applyTheme(theme);
  emit();
}

/**
 * Paint the stored theme and keep following it. Called once, at boot.
 *
 * Returns a teardown so it composes like any other subscription, though in
 * practice nothing unmounts the app.
 */
export function startThemeSync(): () => void {
  applyTheme(current);

  /*
   * Another tab -- the CRM in one window, the Helpdesk in another -- changed
   * the preference. `key` is null when the whole store was cleared, which
   * counts: it means the preference is gone and this tab should fall back with
   * everybody else.
   */
  const onStorage = (event: StorageEvent): void => {
    if (event.key !== null && event.key !== KEY) return;

    const next = read();
    if (next === current) return;

    current = next;
    applyTheme(next);
    emit();
  };

  /*
   * Follow the OS live while on "system". Without this, changing the system
   * theme leaves the app on whatever it resolved to at load -- which looks like
   * the setting is being ignored.
   */
  const media = window.matchMedia("(prefers-color-scheme: dark)");
  const onSystemChange = (): void => {
    if (current === "system") applyTheme("system");
  };

  window.addEventListener("storage", onStorage);
  media.addEventListener("change", onSystemChange);

  return () => {
    window.removeEventListener("storage", onStorage);
    media.removeEventListener("change", onSystemChange);
  };
}

export function useTheme(): [Theme, (theme: Theme) => void] {
  const theme = useSyncExternalStore(subscribe, currentTheme, currentTheme);
  return [theme, setTheme];
}

/**
 * Whether the app is currently rendering dark, as a boolean.
 *
 * Read-only, unlike `useTheme`: a chart needs to know which palette step to
 * paint with, and calling the stateful hook for that would give every chart its
 * own copy of the preference.
 *
 * Watches the class on <html> rather than the store, because that class is the
 * resolved answer -- it already accounts for an explicit light or dark choice
 * overriding the system setting, and for the system flipping under "system".
 */
export function useIsDark(): boolean {
  return useSyncExternalStore(subscribeToClass, isDarkNow, isDarkNow);
}

function isDarkNow(): boolean {
  return (
    typeof document !== "undefined" &&
    document.documentElement.classList.contains("dark")
  );
}

function subscribeToClass(listener: () => void): () => void {
  const observer = new MutationObserver(listener);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["class"],
  });
  return () => observer.disconnect();
}
